// src/domains/core/context-intelligence/registry/indicatorRegistry.ts

import type { Indicator } from "../types/indicators.types";

/**
 * Indicator Registry
 * Central registry for all available indicators
 */
class IndicatorRegistry {
  private indicators = new Map<string, Indicator>();

  /**
   * Register a new indicator
   *
   * @param indicator - Indicator to register
   *
   * @returns void
   *
   */
  register(indicator: Indicator): void {
    this.indicators.set(indicator.id, indicator);
  }

  /**
   * Get an indicator by ID
   *
   * @param id - Indicator ID
   *
   * @returns Indicator or undefined if not found
   *
   */
  get(id: string): Indicator | undefined {
    return this.indicators.get(id);
  }

  /**
   * Get all registered indicators
   *
   * @returns Array of all registered indicators
   *
   */
  getAll(): Indicator[] {
    return [...this.indicators.values()];
  }

  /**
   * Get indicators by category
   *
   * @param category - Indicator category
   *
   * @returns Array of indicators in the specified category
   *
   */
  getByCategory(category: Indicator["category"]): Indicator[] {
    return this.getAll().filter((ind) => ind.category === category);
  }

  /**
   * Get indicators by source
   *
   * @param source - Indicator source
   *
   * @returns Array of indicators from the specified source
   *
   */
  getBySource(source: Indicator["source"]): Indicator[] {
    return this.getAll().filter((ind) => ind.source === source);
  }

  /**
   * Get enabled indicators only
   *
   * @returns Array of enabled indicators
   *
   */
  getEnabled(): Indicator[] {
    return this.getAll().filter((ind) => ind.enabled);
  }

  /**
   * Check if an indicator is registered
   *
   * @param id - Indicator ID
   *
   * @returns true if indicator is registered, false otherwise
   *
   */
  has(id: string): boolean {
    return this.indicators.has(id);
  }
}

/**
 * Singleton Indicator Registry Instance
 */
export const indicatorRegistry = new IndicatorRegistry();

/**
 * Initialize default indicators for PoC (Health + Productivity)
 */
export function initializeIndicatorRegistry(): void {
  // Oura Indicators
  indicatorRegistry.register({
    category: "physical",
    dataType: "numeric",
    defaultAggregation: "latest",
    description: "Overall readiness score from Oura Ring (0-100)",
    enabled: true,
    id: "oura_readiness",
    maxValue: 100,
    minValue: 0,
    name: "Oura Readiness Score",
    source: "oura",
    unit: "score",
  });

  indicatorRegistry.register({
    category: "physical",
    dataType: "numeric",
    defaultAggregation: "latest",
    description: "Daily activity score from Oura Ring (0-100)",
    enabled: true,
    id: "oura_activity",
    maxValue: 100,
    minValue: 0,
    name: "Oura Activity Score",
    source: "oura",
    unit: "score",
  });

  indicatorRegistry.register({
    category: "physical",
    dataType: "numeric",
    defaultAggregation: "latest",
    description: "Sleep quality score from Oura Ring (0-100)",
    enabled: true,
    id: "oura_sleep_score",
    maxValue: 100,
    minValue: 0,
    name: "Oura Sleep Score",
    source: "oura",
    unit: "score",
  });

  indicatorRegistry.register({
    category: "physical",
    dataType: "numeric",
    defaultAggregation: "sum",
    description: "Daily step count from Oura Ring",
    enabled: true,
    id: "oura_steps",
    minValue: 0,
    name: "Oura Daily Steps",
    source: "oura",
    unit: "steps",
  });

  // Withings Indicators
  indicatorRegistry.register({
    category: "physical",
    dataType: "numeric",
    defaultAggregation: "latest",
    description: "Body weight measurement from Withings scale",
    enabled: true,
    id: "withings_weight",
    minValue: 0,
    name: "Withings Weight",
    source: "withings",
    unit: "kg",
  });

  indicatorRegistry.register({
    category: "physical",
    dataType: "numeric",
    defaultAggregation: "latest",
    description: "Body fat percentage from Withings scale",
    enabled: true,
    id: "withings_body_fat",
    maxValue: 100,
    minValue: 0,
    name: "Withings Body Fat",
    source: "withings",
    unit: "%",
  });

  // TimeTracQ Indicators
  indicatorRegistry.register({
    category: "cognitive",
    dataType: "numeric",
    defaultAggregation: "mean",
    description: "Percentage of completed tasks vs total tasks",
    enabled: true,
    id: "timetracq_task_completion_rate",
    maxValue: 100,
    minValue: 0,
    name: "Task Completion Rate",
    source: "timetracq",
    unit: "%",
  });

  indicatorRegistry.register({
    category: "cognitive",
    dataType: "numeric",
    defaultAggregation: "latest",
    description: "Number of open/in-progress tasks",
    enabled: true,
    id: "timetracq_open_tasks",
    minValue: 0,
    name: "Open Tasks Count",
    source: "timetracq",
    unit: "count",
  });

  indicatorRegistry.register({
    category: "cognitive",
    dataType: "numeric",
    defaultAggregation: "latest",
    description: "Total number of tasks (all statuses)",
    enabled: true,
    id: "timetracq_total_tasks",
    minValue: 0,
    name: "Total Tasks Count",
    source: "timetracq",
    unit: "count",
  });
}
